import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from "@angular/core";
import { fadeInOut } from "../../../rubbl-shared/animations";
import { EnvironmentConfig, MarketTrackingEvents, ModalService } from "../../../core/core";
import { InventoryItem } from "../../../inventory/models/inventory-item.model";
import { Attachment } from "../../../inventory/models/attachment.model";
import { MachineCardAddToCartComponent } from "../machine-card-add-to-cart/machine-card-add-to-cart.component";
import { deepClone } from "fast-json-patch/module/core";
import { SwiperConfigInterface } from "ngx-swiper-wrapper";
import { PaginationOptions } from "swiper/types/components/pagination";
import { InventoryService } from "../../../inventory/inventory";

declare const environment: EnvironmentConfig;

@Component({
  selector: "common-machine-card-advanced",
  templateUrl: "./machine-card-advanced.component.html",
  styleUrls: ["./machine-card-advanced.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOut("250ms", "250ms")],
})
export class MachineCardAdvancedComponent implements OnInit, OnChanges {
  @Input() machine: InventoryItem;
  @Input() filteredAttachments: string[] = [];
  @Input() machineIdsInCart: string[];

  machineInCart: boolean;
  MarketTrackingEvents = MarketTrackingEvents;
  fixedAttachments: Attachment[] = [];
  nonFixedAttachments: Attachment[] = [];
  availableAndSelected: Attachment[] = [];

  showExtraInfo: boolean = false;
  extraInfoHeight: string;
  baseImagePath = environment.baseImagePath;
  hasStockPhotos: boolean = false;

  @ViewChild("card") card;

  extraInfoIndex = 0;
  paginationConfig: PaginationOptions = {
    type: "bullets",
    el: ".swiper-pagination",
    bulletActiveClass: "swiper-pagination-bullet-active active-bullet",
    bulletClass: "swiper-pagination-bullet inactive-bullet",
    clickable: true,
    modifierClass: "swiper-pagination-bullets bottom-0",
  };

  extraInfoConfig: SwiperConfigInterface = {
    pagination: this.paginationConfig,
  };

  imageSwipeConfig: SwiperConfigInterface = {
    loop: true,
    pagination: this.paginationConfig,
    scrollbar: false,
    navigation: true,
    lazy: {
      preloaderClass: "preloader",
    },
  };
  machineDetailsUrl: string;

  constructor(private modalService: ModalService, private cd: ChangeDetectorRef, private inventoryService: InventoryService) {}

  ngOnInit() {
    if (this.machine.thumbnails?.length == 1) {
      this.imageSwipeConfig.pagination = false;
      this.imageSwipeConfig.navigation = false;
    }

    const firstThumbNail = this.machine?.thumbnails[0];
    if (firstThumbNail && firstThumbNail.includes("pubweb/catalog")) {
      this.hasStockPhotos = true;
    }

    this.machineDetailsUrl = this.inventoryService.machineDetailsUrl(this.machine, false);

    // filter only available attachments and distinct by display name
    this.machine.relatedAttachments = this.machine.relatedAttachments
      .filter(d => d.status === "Available" || d.status == "Onboarding" || d.status == "OnRent")
      .filter((value, index, self) => index === self.findIndex(t => t.displayName === value.displayName));

    // figure out what is fixed on the attachments
    this.fixedAttachments = this.machine.relatedAttachments.filter(d => d.isFixed);
    this.nonFixedAttachments = this.machine.relatedAttachments.filter(d => !d.isFixed);

    let installFirst = true;
    for (const filteredAttachment of this.filteredAttachments) {
      const attachment = this.machine.relatedAttachments.find(d => d.size == filteredAttachment);
      if (!attachment) {
        continue;
      }
      // if we have a filtered attachment we need to select it
      attachment.selected = true;
      // lets install the first one
      if (installFirst) {
        attachment.install = true;
        installFirst = false;

        // if the attachment is standard set the cost to 0
        if (attachment.standard) {
          attachment.price = attachment.cost = 0;
        }
      }

      this.availableAndSelected.push(attachment);
    }

    // add the rental rate into discounts
    this.machine.rateSchedules.splice(2, 0, { label: "MONTH", numDays: 28, cost: this.machine.rentalRate, discount: 0, discountPercent: 0 });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.machineIdsInCart) {
      this.machineInCart = changes.machineIdsInCart.currentValue.includes(this.machine.id);
    }
  }

  showExtraInfoclick(index: number = 0) {
    this.imageSwipeConfig.pagination = false;
    this.extraInfoHeight = this.card.nativeElement["offsetHeight"] + 10 + "px";
    this.showExtraInfo = true;

    this.extraInfoIndex = index;

    // we query for the modal body because on the search resutls map we slide out a modal.
    // if we dont have a modal body we need to add event listener to the document to hide the extra info
    const modalBody = document.querySelector(".modal-body");
    if (modalBody) {
      modalBody.addEventListener(
        "scroll",
        e => {
          this.hideExtraInfo();
        },
        { once: true },
      );
    } else {
      // add event listener to scroll
      document.addEventListener(
        "scroll",
        e => {
          this.hideExtraInfo();
        },
        { once: true },
      );
    }
  }

  hideExtraInfo() {
    this.imageSwipeConfig.pagination = this.paginationConfig;
    this.showExtraInfo = false;
    this.cd.detectChanges();
  }

  addToCart() {
    const modalRef = this.modalService.standardModal(MachineCardAddToCartComponent, true);
    modalRef.componentInstance.machine = deepClone(this.machine);
    modalRef.componentInstance.machineInCart = this.machineInCart;
    modalRef.result.then(
      result => {},
      () => {},
    );
  }

  showPricingOptions() {
    const hasAttachments = this.fixedAttachments.length || this.nonFixedAttachments.length;
    if (hasAttachments) {
      this.showExtraInfoclick(1);
    } else {
      this.showExtraInfoclick(0);
    }
  }

  showStockPhotoInfo() {
    const hasAttachments = this.fixedAttachments.length || this.nonFixedAttachments.length;
    if (hasAttachments) {
      this.showExtraInfoclick(2);
    } else {
      this.showExtraInfoclick(1);
    }
  }

  showRpoInfo() {
    const hasAttachments = this.fixedAttachments.length || this.nonFixedAttachments.length;
    let indexForRpo = 1;
    hasAttachments ? indexForRpo++ : null;
    this.hasStockPhotos ? indexForRpo++ : null;

    this.showExtraInfoclick(indexForRpo);
  }
}
